'use client';

import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { useLocale } from '@/contexts/LocaleContext';
import { TAX_FIELDS, COUNTRY_LIST, getRegionFromCountry } from '@/lib/geoLocale';

interface BankDetails {
  account_holder_name: string;
  bank_name: string;
  account_number: string;
  ifsc_code: string;
  upi_id: string;
  pan_number: string;
  gst_number: string;
  w8ben_declared: boolean;
  distribution_agreement_signed: boolean;
  distribution_agreement_signed_at: string | null;
}

export default function LegalDocumentsPage() {
  const { selectedCountry, setCountry } = useLocale();
  const [activeTab, setActiveTab] = useState<'bank' | 'tax' | 'agreement'>('bank');
  const [bankForm, setBankForm] = useState({ accountHolderName: '', bankName: '', accountNumber: '', ifscCode: '', upiId: '' });
  const [taxForm, setTaxForm] = useState<Record<string, string | boolean>>({ panNumber: '', gstNumber: '', w8benDeclared: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [agreementSigned, setAgreementSigned] = useState(false);
  const [agreementSignedAt, setAgreementSignedAt] = useState<string | null>(null);
  const [signingAgreement, setSigningAgreement] = useState(false);

  const region = getRegionFromCountry(selectedCountry);
  const taxFields = TAX_FIELDS[region] || TAX_FIELDS['ROW'];
  const isIndia = selectedCountry === 'IN';

  useEffect(() => {
    async function fetchBankDetails() {
      try {
        const res = await fetch('/api/user/bank-details');
        if (!res.ok) return;
        const json = await res.json();
        const d: BankDetails = json.bankDetails;
        if (d) {
          setBankForm({ accountHolderName: d.account_holder_name || '', bankName: d.bank_name || '', accountNumber: d.account_number || '', ifscCode: d.ifsc_code || '', upiId: d.upi_id || '' });
          setTaxForm({ panNumber: d.pan_number || '', gstNumber: d.gst_number || '', w8benDeclared: d.w8ben_declared || false });
          setAgreementSigned(d.distribution_agreement_signed || false);
          setAgreementSignedAt(d.distribution_agreement_signed_at || null);
        }
      } catch (e) {
        console.error('Failed to load bank details:', e);
      } finally {
        setLoading(false);
      }
    }
    fetchBankDetails();
  }, []);

  async function handleSaveBank(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch('/api/user/bank-details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...bankForm, ...taxForm }),
      });
      const json = await res.json();
      if (!res.ok) { setSaveMsg({ type: 'error', text: json.error || 'Save failed' }); return; }
      setSaveMsg({ type: 'success', text: 'Bank details saved successfully!' });
    } catch (e: any) {
      setSaveMsg({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  }

  async function handleSaveTax(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch('/api/user/bank-details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...bankForm, ...taxForm }),
      });
      const json = await res.json();
      if (!res.ok) { setSaveMsg({ type: 'error', text: json.error || 'Save failed' }); return; }
      setSaveMsg({ type: 'success', text: 'Tax details saved successfully!' });
    } catch (e: any) {
      setSaveMsg({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  }

  async function handleSignAgreement() {
    setSigningAgreement(true);
    try {
      const res = await fetch('/api/user/bank-details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...bankForm, ...taxForm, distributionAgreementSigned: true, distributionAgreementSignedAt: new Date().toISOString() }),
      });
      const json = await res.json();
      if (!res.ok) { setSaveMsg({ type: 'error', text: json.error || 'Signing failed' }); return; }
      setAgreementSigned(true);
      setAgreementSignedAt(new Date().toISOString());
      setSaveMsg({ type: 'success', text: 'Distribution agreement signed and timestamped!' });
    } catch (e: any) {
      setSaveMsg({ type: 'error', text: e.message });
    } finally {
      setSigningAgreement(false);
      setTimeout(() => setSaveMsg(null), 4000);
    }
  }

  const inputClass = "w-full px-4 py-2.5 rounded-xl text-sm outline-none transition-all";
  const inputStyle = { background: 'var(--card-elevated)', border: '1px solid var(--border)', color: 'var(--foreground)' };

  const tabs = [
    { id: 'bank', label: isIndia ? 'Bank Details' : 'Payment Details', icon: 'BuildingLibraryIcon' },
    { id: 'tax', label: 'Tax & Compliance', icon: 'DocumentTextIcon' },
    { id: 'agreement', label: 'Distribution Agreement', icon: 'ShieldCheckIcon' },
  ] as const;

  // Region-specific bank form fields
  const bankFields = isIndia
    ? [
        { label: 'Account Holder Name *', key: 'accountHolderName', placeholder: 'Full legal name as per bank' },
        { label: 'Bank Name *', key: 'bankName', placeholder: 'e.g. HDFC Bank, SBI' },
        { label: 'Account Number *', key: 'accountNumber', placeholder: 'Bank account number' },
        { label: 'IFSC Code *', key: 'ifscCode', placeholder: 'e.g. HDFC0001234' },
        { label: 'UPI ID (optional)', key: 'upiId', placeholder: 'e.g. name@upi' },
      ]
    : region === 'GB' || region === 'EU'
    ? [
        { label: 'Account Holder Name *', key: 'accountHolderName', placeholder: 'Full legal name' },
        { label: 'Bank Name *', key: 'bankName', placeholder: 'Bank name' },
        { label: 'IBAN *', key: 'accountNumber', placeholder: 'e.g. GB29NWBK60161331926819' },
        { label: 'BIC / SWIFT Code *', key: 'ifscCode', placeholder: 'e.g. NWBKGB2L' },
        { label: 'PayPal Email (optional)', key: 'upiId', placeholder: 'PayPal email address' },
      ]
    : [
        { label: 'Account Holder Name *', key: 'accountHolderName', placeholder: 'Full legal name' },
        { label: 'Bank Name *', key: 'bankName', placeholder: 'Bank name' },
        { label: 'Account Number / Routing *', key: 'accountNumber', placeholder: 'Account number' },
        { label: 'SWIFT / ABA Routing Code *', key: 'ifscCode', placeholder: 'SWIFT or routing number' },
        { label: 'PayPal Email (optional)', key: 'upiId', placeholder: 'PayPal email address' },
      ];

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>Legal & Compliance</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              {isIndia ? 'Indian bank details, tax identification, and distribution agreement' : 'Payment details, tax identification, and distribution agreement'}
            </p>
          </div>
          {/* Country selector */}
          <div className="flex items-center gap-2">
            <Icon name="GlobeAltIcon" size={16} style={{ color: 'var(--muted-foreground)' }} />
            <select
              value={selectedCountry}
              onChange={(e) => setCountry(e.target.value)}
              className="text-xs px-2 py-1.5 rounded-lg outline-none"
              style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
            >
              {COUNTRY_LIST.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        {saveMsg && (
          <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: saveMsg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${saveMsg.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
            <Icon name={saveMsg.type === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={18} style={{ color: saveMsg.type === 'success' ? '#10b981' : '#ef4444' }} />
            <p className="text-sm font-semibold" style={{ color: saveMsg.type === 'success' ? '#10b981' : '#ef4444' }}>{saveMsg.text}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 rounded-xl w-fit" style={{ background: 'var(--muted)' }}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all" style={{ background: activeTab === t.id ? 'var(--primary)' : 'transparent', color: activeTab === t.id ? 'white' : 'var(--muted-foreground)' }}>
              <Icon name={t.icon as any} size={14} />
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
          </div>
        ) : (
          <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            {/* Bank / Payment Details Tab */}
            {activeTab === 'bank' && (
              <form onSubmit={handleSaveBank} className="space-y-5">
                <h3 className="text-base font-bold mb-4" style={{ color: 'var(--foreground)' }}>
                  {isIndia ? 'Indian Bank Account Details' : region === 'GB' || region === 'EU' ? 'IBAN / SWIFT Bank Details' : 'Bank Account Details'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {bankFields.map((field) => (
                    <div key={field.key}>
                      <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>{field.label}</label>
                      <input
                        value={(bankForm as any)[field.key] || ''}
                        onChange={(e) => setBankForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                        placeholder={field.placeholder}
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end pt-2">
                  <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={{ background: 'var(--primary)' }}>
                    {saving ? 'Saving...' : 'Save Details'}
                  </button>
                </div>
              </form>
            )}

            {/* Tax Tab — Region-specific */}
            {activeTab === 'tax' && (
              <form onSubmit={handleSaveTax} className="space-y-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>Tax Identification</h3>
                  <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: 'rgba(124,58,237,0.12)', color: 'var(--primary-light)' }}>
                    {COUNTRY_LIST.find(c => c.code === selectedCountry)?.name || selectedCountry}
                  </span>
                </div>

                {/* Region-specific tax info banner */}
                <div className="p-3 rounded-xl text-xs" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', color: 'var(--muted-foreground)' }}>
                  {isIndia && '🇮🇳 India: PAN Card required for royalty payments above ₹10,000. GST optional for registered businesses.'}
                  {region === 'US' && '🇺🇸 USA: W-9 for US citizens/residents. W-8BEN for foreign individuals. SSN/EIN required for tax reporting.'}
                  {region === 'GB' && '🇬🇧 UK: VAT number for VAT-registered businesses. National Insurance number for individuals.'}
                  {region === 'EU' && '🇪🇺 Europe: VAT number required for EU businesses. National Tax ID for individuals.'}
                  {region === 'AU' && '🇦🇺 Australia: ABN for businesses. TFN for individuals. Required for ATO tax reporting.'}
                  {region === 'ROW' && '🌍 International: National Tax Identification Number (TIN) required for your country.'}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {taxFields.map((field) => (
                    <div key={field.key}>
                      <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                        {field.label}
                        {field.required && <span className="ml-1" style={{ color: 'var(--danger)' }}>*</span>}
                      </label>
                      <input
                        value={(taxForm[field.key] as string) || ''}
                        onChange={(e) => setTaxForm((p) => ({ ...p, [field.key]: e.target.value }))}
                        placeholder={field.placeholder}
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                  ))}
                </div>

                {/* W-8BEN checkbox for non-US international */}
                {region !== 'IN' && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!taxForm.w8benDeclared}
                      onChange={(e) => setTaxForm((p) => ({ ...p, w8benDeclared: e.target.checked }))}
                      className="mt-0.5"
                    />
                    <span className="text-sm" style={{ color: 'var(--foreground)' }}>
                      I declare W-8BEN compliance for international royalty payments (non-US persons)
                    </span>
                  </label>
                )}

                <div className="flex justify-end pt-2">
                  <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={{ background: 'var(--primary)' }}>
                    {saving ? 'Saving...' : 'Save Tax Details'}
                  </button>
                </div>
              </form>
            )}

            {/* Agreement Tab */}
            {activeTab === 'agreement' && (
              <div className="space-y-5">
                <h3 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>Distribution Agreement</h3>
                <div className="p-5 rounded-xl space-y-3 text-sm" style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}>
                  <p style={{ color: 'var(--muted-foreground)' }}>By signing this agreement, you confirm that:</p>
                  {[
                    'You own or control 100% of the master recording rights for all submitted content.',
                    'You grant TubeNexa a non-exclusive license to distribute your music globally.',
                    'You agree to TubeNexa\'s royalty payment terms and distribution policies.',
                    'All submitted content complies with DSP content guidelines and copyright laws.',
                  ].map((point, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Icon name="CheckCircleIcon" size={16} style={{ color: '#10b981', flexShrink: 0, marginTop: 2 }} />
                      <span style={{ color: 'var(--foreground)' }}>{point}</span>
                    </div>
                  ))}
                </div>
                {agreementSigned ? (
                  <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
                    <Icon name="ShieldCheckIcon" size={20} style={{ color: '#10b981' }} />
                    <div>
                      <p className="text-sm font-bold" style={{ color: '#10b981' }}>Agreement Signed</p>
                      {agreementSignedAt && (
                        <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                          Digital signature timestamp: {new Date(agreementSignedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <button onClick={handleSignAgreement} disabled={signingAgreement} className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={{ background: 'var(--primary)' }}>
                    {signingAgreement ? 'Signing...' : 'Agree & Auto-Sign Distribution Agreement'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
